import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import BatchResults, { type CreationResult } from '@/components/admin/BatchResults'
import {
  ArrowLeft,
  UserPlus,
  Users,
  Upload,
  Loader2,
  CheckCircle,
  XCircle,
  Trash2,
  Download,
  FileText,
  AlertCircle
} from 'lucide-react'

interface TeacherForm {
  email: string
  password: string
  first_name: string
  last_name: string
  phone: string
  bio: string
  specializations: string
  years_experience: string
  certifications: string
}

const emptyForm: TeacherForm = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  phone: '',
  bio: '',
  specializations: '',
  years_experience: '',
  certifications: ''
}

type Tab = 'single' | 'batch' | 'import'

export default function TeacherCreate() {
  const [activeTab, setActiveTab] = useState<Tab>('single')
  const [form, setForm] = useState<TeacherForm>({ ...emptyForm })
  const [isCreating, setIsCreating] = useState(false)
  const [singleResult, setSingleResult] = useState<CreationResult | null>(null)

  // Batch state
  const [batchList, setBatchList] = useState<TeacherForm[]>([])
  const [batchResults, setBatchResults] = useState<CreationResult[]>([])
  const [batchProgress, setBatchProgress] = useState(0)
  const [isBatchRunning, setIsBatchRunning] = useState(false)

  // Import state
  const [csvData, setCsvData] = useState<TeacherForm[]>([])
  const [csvError, setCsvError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const updateForm = (field: keyof TeacherForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const validateForm = (f: TeacherForm): string | null => {
    if (!f.email || !f.email.includes('@')) return 'Email inválido'
    if (!f.password || f.password.length < 6) return 'Contraseña mínimo 6 caracteres'
    if (!f.first_name.trim()) return 'Nombre requerido'
    if (!f.last_name.trim()) return 'Apellido requerido'
    return null
  }

  const parseCommaSeparated = (value: string): string[] => {
    return value.split(',').map(s => s.trim()).filter(s => s.length > 0)
  }

  const createTeacher = async (data: TeacherForm): Promise<CreationResult> => {
    try {
      const { data: result, error } = await (supabase.rpc as any)('admin_create_teacher', {
        p_email: data.email.trim(),
        p_password: data.password,
        p_first_name: data.first_name.trim(),
        p_last_name: data.last_name.trim(),
        p_phone: data.phone.trim() || null,
        p_bio: data.bio.trim() || null,
        p_specializations: parseCommaSeparated(data.specializations),
        p_years_experience: data.years_experience ? Number.parseInt(data.years_experience) : null,
        p_certifications: parseCommaSeparated(data.certifications)
      })

      if (error) {
        return { email: data.email, name: `${data.first_name} ${data.last_name}`, success: false, error: error.message }
      }

      if (result && !result.success) {
        return { email: data.email, name: `${data.first_name} ${data.last_name}`, success: false, error: result.error }
      }

      return { email: data.email, name: result?.name || `${data.first_name} ${data.last_name}`, success: true }
    } catch (err: any) {
      return { email: data.email, name: `${data.first_name} ${data.last_name}`, success: false, error: err.message }
    }
  }

  // === SINGLE ===
  const handleCreateSingle = async () => {
    const err = validateForm(form)
    if (err) {
      setSingleResult({ email: form.email, name: '', success: false, error: err })
      return
    }

    setIsCreating(true)
    setSingleResult(null)
    const result = await createTeacher(form)
    setSingleResult(result)
    if (result.success) {
      setForm({ ...emptyForm })
    }
    setIsCreating(false)
  }

  // === BATCH ===
  const handleAddToBatch = () => {
    const err = validateForm(form)
    if (err) {
      setSingleResult({ email: form.email, name: '', success: false, error: err })
      return
    }

    if (batchList.some(s => s.email.toLowerCase() === form.email.toLowerCase())) {
      setSingleResult({ email: form.email, name: '', success: false, error: 'Este email ya esta en la lista' })
      return
    }

    setBatchList(prev => [...prev, { ...form }])
    setForm({ ...emptyForm })
    setSingleResult(null)
  }

  const handleRemoveFromBatch = (index: number) => {
    setBatchList(prev => prev.filter((_, i) => i !== index))
  }

  const handleCreateBatch = async () => {
    if (batchList.length === 0) return

    setIsBatchRunning(true)
    setBatchResults([])
    setBatchProgress(0)

    const results: CreationResult[] = []
    for (let i = 0; i < batchList.length; i++) {
      const result = await createTeacher(batchList[i])
      results.push(result)
      setBatchResults([...results])
      setBatchProgress(Math.round(((i + 1) / batchList.length) * 100))
    }

    const failedEmails = results.filter(r => !r.success).map(r => r.email.toLowerCase())
    setBatchList(prev => prev.filter(s => failedEmails.includes(s.email.toLowerCase())))
    setIsBatchRunning(false)
  }

  // === IMPORT ===
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setCsvError('')
    setCsvData([])

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string
        const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0)

        if (lines.length < 2) {
          setCsvError('El archivo debe tener al menos una fila de encabezado y una de datos')
          return
        }

        const dataLines = lines.slice(1)
        const parsed: TeacherForm[] = []

        for (let i = 0; i < dataLines.length; i++) {
          const cols = dataLines[i].split(',').map(c => c.trim().replaceAll(/^["']|["']$/g, ''))


          if (cols.length < 4) {
            setCsvError(`Fila ${i + 2}: faltan columnas (mínimo 4: email, password, nombre, apellido)`)
            return
          }

          parsed.push({
            email: cols[0],
            password: cols[1],
            first_name: cols[2],
            last_name: cols[3],
            phone: cols[4] || '',
            bio: cols[5] || '',
            specializations: cols[6] || '',
            years_experience: cols[7] || '',
            certifications: cols[8] || ''
          })
        }

        setCsvData(parsed)
      } catch {
        setCsvError('Error al leer el archivo. Verifica que sea un CSV válido.')
      }
    }
    reader.readAsText(file)
  }

  const handleImportCsv = async () => {
    if (csvData.length === 0) return

    setIsBatchRunning(true)
    setBatchResults([])
    setBatchProgress(0)

    const results: CreationResult[] = []
    for (let i = 0; i < csvData.length; i++) {
      const result = await createTeacher(csvData[i])
      results.push(result)
      setBatchResults([...results])
      setBatchProgress(Math.round(((i + 1) / csvData.length) * 100))
    }

    const failedEmails = results.filter(r => !r.success).map(r => r.email.toLowerCase())
    setCsvData(prev => prev.filter(s => failedEmails.includes(s.email.toLowerCase())))
    setIsBatchRunning(false)
  }

  const downloadTemplate = () => {
    const csv = 'email,password,nombre,apellido,telefono,bio,especializaciones,experiencia,certificaciones\nprofesor@ejemplo.com,Password123,Jean,Dupont,+56912345678,Profesor nativo de francés,pronunciación;gramática,5,DELF;DALF'
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'plantilla_profesores.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const tabs = [
    { id: 'single' as Tab, label: 'Crear profesor', icon: UserPlus },
    { id: 'batch' as Tab, label: 'Crear varios', icon: Users },
    { id: 'import' as Tab, label: 'Importar CSV', icon: Upload },
  ]

  const renderForm = (buttonLabel: string, onSubmit: () => void) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="teacher-email" className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input id="teacher-email" type="email" value={form.email}
            onChange={e => updateForm('email', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="profesor@email.com"
          />
        </div>
        <div>
          <label htmlFor="teacher-password" className="block text-sm font-medium text-gray-700 mb-1">Contraseña *</label>
          <input
            id="teacher-password"
            type="text"
            value={form.password}
            onChange={e => updateForm('password', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="Mínimo 6 caracteres"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="teacher-first-name" className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
          <input id="teacher-first-name" type="text" value={form.first_name}
            onChange={e => updateForm('first_name', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="Nombre"
          />
        </div>
        <div>
          <label htmlFor="teacher-last-name" className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
          <input
            id="teacher-last-name"
            type="text"
            value={form.last_name}
            onChange={e => updateForm('last_name', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="Apellido"
          />
        </div>
      </div>

      <div>
        <label htmlFor="teacher-phone" className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
        <input
          id="teacher-phone"
          type="tel"
          value={form.phone}
          onChange={e => updateForm('phone', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          placeholder="+56 9 1234 5678"
        />
      </div>

      <div>
        <label htmlFor="teacher-bio"  className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
        <textarea
          id="teacher-bio"
          value={form.bio}
          onChange={e => updateForm('bio', e.target.value)}
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
          placeholder="Breve descripción del profesor (opcional)"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="teacher-specializations" className="block text-sm font-medium text-gray-700 mb-1">Especializaciones</label>
          <input
            id="teacher-specializations"
            type="text"
            value={form.specializations}
            onChange={e => updateForm('specializations', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="pronunciación, gramática, conversación"
          />
          <p className="text-xs text-gray-500 mt-1">Separar con comas</p>
        </div>
        <div>
          <label htmlFor="teacher-experience" className="block text-sm font-medium text-gray-700 mb-1">Años de experiencia</label>
          <input
            id="teacher-experience"
            type="number"
            min="0"
            value={form.years_experience}
            onChange={e => updateForm('years_experience', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="Ej: 5"
          />
        </div>
        <div>
          <label htmlFor="teacher-certifications" className="block text-sm font-medium text-gray-700 mb-1">Certificaciones</label>
          <input
            id="teacher-certifications"
            type="text"
            value={form.certifications}
            onChange={e => updateForm('certifications', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
            placeholder="DELF, DALF, TCF"
          />
          <p className="text-xs text-gray-500 mt-1">Separar con comas</p>
        </div>
      </div>

      {singleResult && (
        <div className={`flex items-center gap-2 p-3 rounded-lg ${
          singleResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {singleResult.success ? <CheckCircle className="w-5 h-5 shrink-0" /> : <XCircle className="w-5 h-5 shrink-0" />}
          <span className="text-sm">
            {singleResult.success
              ? `Profesor ${singleResult.name} creado exitosamente`
              : singleResult.error}
          </span>
        </div>
      )}

      <button
        onClick={onSubmit}
        disabled={isCreating}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0055A4] text-white rounded-lg font-medium hover:bg-[#004080] disabled:opacity-50 transition-colors"
      >
        {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
        {buttonLabel}
      </button>
    </div>
  )

  const renderBatchResults = () => (
    <BatchResults results={batchResults} progress={batchProgress} isRunning={isBatchRunning} />
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a usuarios
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Crear profesores</h1>
        <p className="text-gray-600">Crea profesores individualmente, en grupo o importa desde CSV</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id)
              setSingleResult(null)
              setBatchResults([])
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-[#0055A4] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border p-6">

        {/* TAB: Single */}
        {activeTab === 'single' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Crear un profesor</h2>
            {renderForm('Crear profesor', handleCreateSingle)}
          </div>
        )}

        {/* TAB: Batch */}
        {activeTab === 'batch' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-4">Agregar profesores a la lista</h2>
              {renderForm('+ Agregar a la lista', handleAddToBatch)}
            </div>

            {batchList.length > 0 && (
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-3">
                  Lista de profesores ({batchList.length})
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Especializaciones</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Exp.</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {batchList.map((s, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                          <td className="px-4 py-2">{s.email}</td>
                          <td className="px-4 py-2">{s.first_name} {s.last_name}</td>
                          <td className="px-4 py-2 text-gray-600">{s.specializations || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{s.years_experience || '—'}</td>
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={() => handleRemoveFromBatch(i)}
                              disabled={isBatchRunning}
                              className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <button
                    onClick={handleCreateBatch}
                    disabled={isBatchRunning || batchList.length === 0}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {isBatchRunning && <Loader2 className="w-4 h-4 animate-spin" />}
                    Crear {batchList.length} profesor{batchList.length !== 1 ? 'es' : ''}
                  </button>
                  {!isBatchRunning && batchList.length > 0 && (
                    <button
                      onClick={() => { setBatchList([]); setBatchResults([]) }}
                      className="text-sm text-gray-500 hover:text-red-500"
                    >
                      Limpiar lista
                    </button>
                  )}
                </div>

                {renderBatchResults()}
              </div>
            )}
          </div>
        )}

        {/* TAB: Import */}
        {activeTab === 'import' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">Importar desde CSV</h2>
              <p className="text-sm text-gray-600 mb-4">
                Sube un archivo CSV con los datos de los profesores. La primera fila debe ser el encabezado.
              </p>
            </div>

            <div className="bg-[#0055A4]/5 border border-[#0055A4]/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#0055A4] shrink-0 mt-0.5" />
                <div className="text-sm text-[#003d7a]">
                  <p className="font-medium mb-1">Formato del CSV:</p>
                  <code className="text-xs bg-[#0055A4]/10 px-2 py-1 rounded block mb-2">
                    email,password,nombre,apellido,telefono,bio,especializaciones,experiencia,certificaciones
                  </code>
                  <p className="text-xs">
                    <strong>especializaciones/certificaciones</strong>: separar con punto y coma (;) &nbsp;&nbsp;{' '}
                    <strong>experiencia</strong>: número entero
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex-1 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#0055A4]/40 hover:bg-[#0055A4]/5 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="font-medium text-gray-700">Arrastra un archivo CSV o haz clic para seleccionar</p>
                <p className="text-sm text-gray-500 mt-1">Formato: .csv</p>
              </label>
            </div>

            <button
              onClick={downloadTemplate}
              className="inline-flex items-center gap-2 text-sm text-[#0055A4] hover:text-[#003d7a]"
            >
              <Download className="w-4 h-4" />
              Descargar plantilla CSV
            </button>

            {csvError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg">
                <XCircle className="w-5 h-5 shrink-0" />
                <span className="text-sm">{csvError}</span>
              </div>
            )}

            {csvData.length > 0 && (
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-3">
                  Vista previa ({csvData.length} profesores)
                </h3>
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Especializaciones</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Exp.</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Certificaciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {csvData.map((s, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                          <td className="px-4 py-2">{s.email}</td>
                          <td className="px-4 py-2">{s.first_name} {s.last_name}</td>
                          <td className="px-4 py-2 text-gray-600">{s.specializations || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{s.years_experience || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{s.certifications || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <button
                    onClick={handleImportCsv}
                    disabled={isBatchRunning || csvData.length === 0}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {isBatchRunning && <Loader2 className="w-4 h-4 animate-spin" />}
                    Importar {csvData.length} profesor{csvData.length !== 1 ? 'es' : ''}
                  </button>
                  {!isBatchRunning && (
                    <button
                      onClick={() => {
                        setCsvData([])
                        setBatchResults([])
                        if (fileInputRef.current) fileInputRef.current.value = ''
                      }}
                      className="text-sm text-gray-500 hover:text-red-500"
                    >
                      Limpiar
                    </button>
                  )}
                </div>

                {renderBatchResults()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
